import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Scissors, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function AuthPage() {
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("tab") === "register" ? "register" : "login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (tab === "login") {
        const user = await login(email, password);
        toast.success("Connexion reussie !");
        navigate(user.onboarding_complete ? "/tableau-de-bord" : "/onboarding");
      } else {
        if (!name.trim()) { toast.error("Entrez votre nom"); setLoading(false); return; }
        await register(email, password, name);
        toast.success("Compte cree !");
        navigate("/onboarding");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Une erreur est survenue");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-marketing-bg flex items-center justify-center p-6" data-testid="auth-page">
      <div className="w-full max-w-md">
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8"
          data-testid="back-to-home"
        >
          <ArrowLeft size={16} />
          Retour
        </button>

        <div className="bg-white rounded-2xl border border-black/5 shadow-soft p-8">
          <div className="flex items-center gap-2 mb-8">
            <Scissors size={22} className="text-primary" />
            <span className="font-heading font-bold text-xl">CutIt</span>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mb-8 bg-[#F4F4F5] p-1 rounded-lg">
            <button
              onClick={() => setTab("login")}
              className={`flex-1 text-sm font-medium py-2.5 rounded-md transition-all ${tab === "login" ? "bg-white shadow-sm text-foreground" : "text-muted-foreground"}`}
              data-testid="login-tab"
            >
              Connexion
            </button>
            <button
              onClick={() => setTab("register")}
              className={`flex-1 text-sm font-medium py-2.5 rounded-md transition-all ${tab === "register" ? "bg-white shadow-sm text-foreground" : "text-muted-foreground"}`}
              data-testid="register-tab"
            >
              Inscription
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {tab === "register" && (
              <div>
                <Label htmlFor="name" className="text-sm font-medium mb-2 block">Nom</Label>
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Votre nom"
                  className="h-12"
                  data-testid="name-input"
                />
              </div>
            )}
            <div>
              <Label htmlFor="email" className="text-sm font-medium mb-2 block">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="votre@email.com"
                required
                className="h-12"
                data-testid="email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-sm font-medium mb-2 block">Mot de passe</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Min. 6 caracteres"
                required
                className="h-12"
                data-testid="password-input"
              />
            </div>
            <Button
              type="submit"
              className="w-full h-12 rounded-full bg-primary hover:bg-blue-700 font-semibold text-sm"
              disabled={loading}
              data-testid="auth-submit-btn"
            >
              {loading ? "Chargement..." : tab === "login" ? "Se connecter" : "Creer mon compte"}
            </Button>
          </form>

          {tab === "login" && (
            <p className="text-center text-sm text-muted-foreground mt-6">
              Pas encore de compte ?{" "}
              <button onClick={() => setTab("register")} className="text-primary font-medium hover:underline" data-testid="switch-to-register">
                Inscrivez-vous
              </button>
            </p>
          )}
          {tab === "register" && (
            <p className="text-center text-sm text-muted-foreground mt-6">
              Deja un compte ?{" "}
              <button onClick={() => setTab("login")} className="text-primary font-medium hover:underline" data-testid="switch-to-login">
                Connectez-vous
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
